import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Info, ArrowUpRight, WarningCircle } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { money, pct, changeColor, shortDate } from './investorFormat';

const Metric = ({ label, value, tone }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.14em] text-foreground-muted">{label}</p>
    <p className={`text-base md:text-lg font-semibold mt-1 tabular-nums ${tone || 'text-slate-900'}`}>{value}</p>
  </div>
);

/** Personal portfolio value header - the dominant number of the investor hub. */
export const PortfolioValueHeader = ({ portfolio, valuation, title = 'Your portfolio', eyebrow }) => (
  <section data-testid="portfolio-value-header">
    {eyebrow && <p className="text-sm text-foreground-muted">{eyebrow}</p>}
    <p className="text-[11px] uppercase tracking-[0.16em] text-foreground-muted mt-1">{title}</p>

    <div className="mt-2.5 flex items-end gap-3 flex-wrap">
      <p
        className="text-[44px] md:text-[60px] leading-none font-semibold tracking-tight text-slate-900 tabular-nums"
        data-testid="portfolio-value"
      >
        {money(portfolio.current_value)}
      </p>
      {portfolio.total_invested > 0 && (
        <p className={`text-base md:text-lg font-medium tabular-nums mb-1 ${changeColor(portfolio.total_change)}`} data-testid="portfolio-change">
          {money(portfolio.total_change, { signed: true })} · {pct(portfolio.total_change_percent, { signed: true })}
        </p>
      )}
    </div>

    <div className="mt-2 flex items-center gap-2 text-sm text-foreground-muted">
      <span>Portfolio value</span>
      {valuation && (
        <Popover>
          <PopoverTrigger asChild>
            <button className="inline-flex items-center gap-1 underline decoration-dotted decoration-slate-300 underline-offset-2" data-testid="valuation-info-btn">
              <Info size={13} /> How is this calculated?
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 text-xs leading-relaxed" data-testid="valuation-info">
            <span className="block text-[10px] uppercase tracking-[0.14em] text-foreground-muted mb-1.5">Valuation</span>
            {valuation.valuation_basis}
            {valuation.valuation_date && (
              <span className="block mt-2 text-foreground-muted">Last valued {shortDate(valuation.valuation_date)}</span>
            )}
            <span className="block mt-2 text-foreground-muted">
              Estimated values are not realised until an exit or distribution occurs.
            </span>
          </PopoverContent>
        </Popover>
      )}
    </div>

    <div className="mt-7 grid grid-cols-2 gap-6 md:flex md:gap-14" data-testid="portfolio-metrics">
      <Metric label="Total invested" value={money(portfolio.total_invested)} />
      <Metric label="Returns received" value={money(portfolio.income_received)} tone="text-emerald-600" />
      <Metric
        label="Unrealised change"
        value={money(portfolio.unrealized_change, { signed: portfolio.unrealized_change !== 0 })}
        tone={changeColor(portfolio.unrealized_change)}
      />
      <Metric label="Active investments" value={portfolio.active_investments} />
    </div>
  </section>
);

/** Investor exception management - rendered only when something needs action. */
export const NeedsAttention = ({ items }) => {
  const navigate = useNavigate();
  if (!items || items.length === 0) return null;

  return (
    <section className="space-y-2" data-testid="investor-needs-attention">
      <p className="text-[11px] uppercase tracking-[0.16em] text-foreground-muted">Needs your attention</p>
      {items.map((item) => (
        <button
          key={item.id}
          onClick={() => navigate(item.action_route)}
          className="w-full text-left bg-white rounded-2xl border border-black/[0.05] px-5 py-4 flex items-center gap-4 hover:border-primary/40 transition-colors"
          data-testid={`attention-${item.id}`}
        >
          <WarningCircle
            size={20}
            weight="duotone"
            className={item.priority === 'high' ? 'text-amber-500' : 'text-slate-400'}
          />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-slate-900">{item.title}</span>
            <span className="block text-sm text-foreground-muted truncate">{item.description}</span>
          </span>
          <span className="text-sm font-medium text-primary inline-flex items-center gap-1 flex-shrink-0">
            {item.action_label} <ArrowUpRight size={14} weight="bold" />
          </span>
        </button>
      ))}
    </section>
  );
};
